import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-rotate";
import { catMap, markerEmoji, markerHtml } from "@/lib/categories";
import { weekFrom, openInfo } from "@/lib/hours";
import { toast } from "sonner";


const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const PIN = `<svg style="flex-shrink:0" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#64748b" stroke-width="2"><path d="M12 21s7-6.5 7-12a7 7 0 0 0-14 0c0 5.5 7 12 7 12z"/><circle cx="12" cy="9" r="2.5"/></svg>`;

function openStatus(week) {
  const i = openInfo(week);
  if (!i) return "";
  const green = (t) => `<span style="color:#16a34a;font-weight:700">🟢 ${t}</span>`;
  const red = (t) => `<span style="color:#dc2626;font-weight:700">🔴 ${t}</span>`;
  if (i.state === "closed_today") return red("Mbyllur sot");
  if (i.state === "h24") return green("Hapur 24 orë");
  if (i.state === "closed") return i.later ? red(`Mbyllur · Hapet ${esc(i.from)}`) : red("Mbyllur");
  if (i.state === "closing") return red(`Mbyllet ${esc(i.to)}`);
  return `${green("Hapur")}<br/><span style="color:#475569">Mbyllet ${esc(i.to)}</span>`;
}

function popupHtml(p) {
  const avatar = p.logo
    ? `<img src="${esc(p.logo)}" style="width:100%;height:100%;object-fit:cover;transform:scale(${Number(p.logo_scale) || 1})"/>`
    : `<span style="font-size:20px;font-weight:800;color:#0f172a">${esc((p.title || "?")[0].toUpperCase())}</span>`;
  const status = openStatus(weekFrom(p));
  return `<div data-id="${esc(p.id)}" data-testid="map-popup" style="cursor:pointer;width:150px">
    <div style="display:flex;align-items:center;gap:8px">
      <div style="width:48px;height:48px;flex-shrink:0;border-radius:50%;border:2px solid #facc15;overflow:hidden;display:flex;align-items:center;justify-content:center;background:#fff">${avatar}</div>
      <div data-testid="map-popup-status" style="font-size:11px;line-height:1.35;min-width:0">${status || ""}</div>
    </div>
    <div style="font-weight:800;font-size:13px;color:#0f172a;line-height:1.2;margin-top:6px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(p.title)}</div>
    ${p.city ? `<div style="display:flex;align-items:center;gap:3px;font-size:11px;color:#64748b;margin-top:2px">${PIN}${esc(p.city)}</div>` : ""}
    <div style="color:#10b981;font-weight:700;font-size:12px;margin-top:4px">Detajet →</div>
  </div>`;
}

const COMPASS_SVG = `<svg width="20" height="20" viewBox="0 0 24 24"><polygon points="12,2 16,12 12,10.5 8,12" fill="#ef4444"/><polygon points="12,22 16,12 12,13.5 8,12" fill="#94a3b8"/></svg>`;

function addCompass(map) {
  let on = false, btn = null, needle = null, last = null, raf = 0, perm = false, courseAt = 0;
  const apply = (b) => { map.setBearing(b); if (needle) needle.style.transform = `rotate(${b}deg)`; };
  // GPS course while moving: direction of travel always points up
  const setCourse = (deg) => {
    courseAt = Date.now();
    if (!on) return;
    last = (360 - deg) % 360;
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => { if (on) apply(last); });
  };
  const onOri = (e) => {
    if (Date.now() - courseAt < 6000) return;
    const h = e.webkitCompassHeading != null ? e.webkitCompassHeading : (e.absolute || e.type === "deviceorientationabsolute") && e.alpha != null ? 360 - e.alpha : null;
    if (h == null || !Number.isFinite(h)) return;
    const so = (window.screen?.orientation?.angle ?? window.orientation ?? 0) || 0;
    const b = (360 - ((h + so) % 360)) % 360;
    if (last != null && Math.abs(((b - last + 540) % 360) - 180) < 1.5) return;
    last = b;
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => { if (on) apply(b); });
  };
  const evt = "ondeviceorientationabsolute" in window ? "deviceorientationabsolute" : "deviceorientation";
  const listen = () => window.addEventListener(evt, onOri, true);
  const needsPerm = typeof window.DeviceOrientationEvent?.requestPermission === "function";
  const ask = () => window.DeviceOrientationEvent.requestPermission().then((r) => { if (r === "granted") { perm = true; if (on) listen(); } }).catch(() => {});
  const setOn = (v) => {
    on = v;
    btn && btn.classList.toggle("on", v);
    window.removeEventListener(evt, onOri, true);
    if (v) { if (!needsPerm || perm) listen(); } else { last = null; apply(0); }
  };
  const firstTouch = () => { if (needsPerm && !perm && on) ask(); };
  map.getContainer().addEventListener("touchend", firstTouch, { once: true });
  map.getContainer().addEventListener("click", firstTouch, { once: true });
  const Ctl = L.Control.extend({
    onAdd() {
      const wrap = L.DomUtil.create("div", "leaflet-bar");
      btn = L.DomUtil.create("a", "gs-locate-btn gs-compass-btn", wrap);
      btn.href = "#";
      btn.title = "Busulla";
      btn.setAttribute("data-testid", "map-compass-btn");
      btn.innerHTML = `<span style="display:flex;transition:transform .2s">${COMPASS_SVG}</span>`;
      needle = btn.firstChild;
      L.DomEvent.disableClickPropagation(wrap);
      L.DomEvent.on(btn, "click", (e) => { L.DomEvent.preventDefault(e); if (!on && needsPerm && !perm) ask(); setOn(!on); });
      return wrap;
    },
  });
  new Ctl({ position: "topleft" }).addTo(map);
  setOn(true);
  if (needsPerm) ask();
  return { setCourse, stop: () => { window.removeEventListener(evt, onOri, true); cancelAnimationFrame(raf); } };
}

function keepAwake() {
  let lock = null, dead = false;
  const get = () => {
    if (dead || document.visibilityState !== "visible" || !navigator.wakeLock) return;
    navigator.wakeLock.request("screen").then((l) => { if (dead) l.release(); else lock = l; }).catch(() => {});
  };
  const onVis = () => { if (document.visibilityState === "visible") get(); };
  document.addEventListener("visibilitychange", onVis);
  get();
  return () => { dead = true; document.removeEventListener("visibilitychange", onVis); lock && lock.release().catch(() => {}); };
}

const bearingTo = (a, b) => {
  const r = Math.PI / 180, y = Math.sin((b[1] - a[1]) * r) * Math.cos(b[0] * r);
  const x = Math.cos(a[0] * r) * Math.sin(b[0] * r) - Math.sin(a[0] * r) * Math.cos(b[0] * r) * Math.cos((b[1] - a[1]) * r);
  return (Math.atan2(y, x) / r + 360) % 360;
};

const LOCATE_SVG = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><circle cx="12" cy="12" r="4" fill="currentColor"/><circle cx="12" cy="12" r="8"/><line x1="12" y1="1" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="23"/><line x1="1" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="23" y2="12"/></svg>`;

export function MapView({ points = [], center = [56.6745, 12.8568], zoom = 6, height = 320, interactive = true, locate = false, onLocate }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const markersRef = useRef([]);
  const followRef = useRef(false);
  const locatedRef = useRef(false);
  const onLocateRef = useRef(onLocate);
  onLocateRef.current = onLocate;
  const nav = useNavigate();

  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const map = L.map(ref.current, { zoomControl: interactive, scrollWheelZoom: interactive, dragging: interactive,
      ...(locate ? { rotate: true, rotateControl: false, touchRotate: false, shiftKeyRotate: false, bearing: 0 } : {}) }).setView(center, zoom);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap", maxZoom: 19,
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    let watchId = null;
    let compass = null;
    let release = null;
    if (locate) release = keepAwake();
    if (locate && navigator.geolocation) {
      let me = null;
      let prev = null;
      let acc = null;
      let btn = null;
      const setOn = (on) => { followRef.current = on; btn && btn.classList.toggle("on", on); };
      const stop = () => { if (watchId !== null) navigator.geolocation.clearWatch(watchId); watchId = null; setOn(false); };
      const course = (pos, ll) => {
        const c = pos.coords;
        if (c.heading != null && Number.isFinite(c.heading) && (c.speed || 0) > 1) { compass && compass.setCourse(c.heading); prev = ll; return; }
        if (!prev) { prev = ll; return; }
        if (map.distance(prev, ll) > Math.max(8, (c.accuracy || 0) * 0.8)) { compass && compass.setCourse(bearingTo(prev, ll)); prev = ll; }
      };
      const onPos = (pos) => {
        const ll = [pos.coords.latitude, pos.coords.longitude];
        if (!me) {
          me = L.marker(ll, { icon: L.divIcon({ className: "", html: '<div class="gs-me-dot"></div>', iconSize: [18, 18], iconAnchor: [9, 9] }), zIndexOffset: 1000 }).addTo(map);
          acc = L.circle(ll, { radius: pos.coords.accuracy, color: "#2563eb", weight: 1, fillOpacity: 0.08 }).addTo(map);
          map.setView(ll, 16);
        } else {
          me.setLatLng(ll);
          acc.setLatLng(ll).setRadius(pos.coords.accuracy);
          if (followRef.current) map.panTo(ll, { animate: true, duration: 0.5 });
        }
        course(pos, ll);
        locatedRef.current = true;
        onLocateRef.current && onLocateRef.current({ lat: ll[0], lng: ll[1] });
      };
      const start = () => {
        setOn(true);
        if (me) map.setView(me.getLatLng(), Math.max(map.getZoom(), 16));
        if (watchId === null) {
          watchId = navigator.geolocation.watchPosition(onPos, (err) => { if (err.code === 1) { stop(); toast.error("Lejo vendndodhjen (GPS) te browser-i për këtë faqe."); } },
            { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 });
        }
      };
      const Ctl = L.Control.extend({
        onAdd() {
          const wrap = L.DomUtil.create("div", "leaflet-bar");
          btn = L.DomUtil.create("a", "gs-locate-btn", wrap);
          btn.href = "#";
          btn.title = "Vendndodhja ime";
          btn.setAttribute("data-testid", "map-locate-btn");
          btn.innerHTML = LOCATE_SVG;
          L.DomEvent.disableClickPropagation(wrap);
          L.DomEvent.on(btn, "click", (e) => { L.DomEvent.preventDefault(e); start(); });
          return wrap;
        },
      });
      new Ctl({ position: "topleft" }).addTo(map);
      compass = addCompass(map);
      start();
      map.on("dragstart", () => { if (followRef.current) setOn(false); });
      // While following and zoomed in, briefly show places that come into view
      const seen = new Set(), queue = [];
      let busy = false;
      const next = () => {
        const m = queue.shift();
        if (!m) { busy = false; return; }
        busy = true;
        if (!map.getBounds().contains(m.getLatLng())) return next();
        const pp = m.getPopup();
        pp.options.autoPan = false;
        m.openPopup();
        setTimeout(() => { m.closePopup(); pp.options.autoPan = true; next(); }, 2000);
      };
      map.on("moveend", () => {
        const close = followRef.current && map.getZoom() >= 15;
        const b = map.getBounds();
        markersRef.current.forEach((m) => {
          const inside = b.contains(m.getLatLng());
          if (!inside) { seen.delete(m); return; }
          if (close && !seen.has(m)) { seen.add(m); queue.push(m); }
        });
        if (!busy) next();
      });
    }
    return () => { if (watchId !== null) navigator.geolocation.clearWatch(watchId); compass && compass.stop(); release && release(); map.remove(); mapRef.current = null; };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!mapRef.current || !layerRef.current) return;
    layerRef.current.clearLayers();
    markersRef.current = [];
    const valid = points.filter((p) => p.lat && p.lng);
    valid.forEach((p) => {
      const cat = catMap[p.category] || catMap.sherbime;
      const icon = L.divIcon({
        className: "",
        html: `<div class="gs-pin" style="background:${cat.marker}"><span>${markerHtml(markerEmoji(p.category, p.subcategory))}</span></div>`,
        iconSize: [30, 30], iconAnchor: [15, 30],
      });
      const m = L.marker([p.lat, p.lng], { icon }).addTo(layerRef.current);
      markersRef.current.push(m);
      m.bindPopup(popupHtml(p), { className: "gs-popup", minWidth: 150, closeButton: false });
      m.on("popupopen", (e) => {
        const el = e.popup.getElement().querySelector("[data-id]");
        if (el) el.onclick = () => nav(`/shpallje/${p.id}`);
      });
    });
    if (locatedRef.current) return;
    if (valid.length > 1) {
      try { mapRef.current.fitBounds(L.latLngBounds(valid.map((p) => [p.lat, p.lng])).pad(0.2)); } catch {}
    } else if (valid.length === 1) {
      mapRef.current.setView([valid[0].lat, valid[0].lng], 13);
    }
  }, [points, nav]);

  useEffect(() => { mapRef.current && mapRef.current.invalidateSize(); }, [height]);

  return <div ref={ref} data-testid="map-view" style={{ height, width: "100%", borderRadius: 16, zIndex: 0 }} />;
}

export default MapView;
